import { useEffect, useState } from "react";
import AnimateOnScroll from "@/components/AnimateOnScroll";
import { MDXProvider } from "@mdx-js/react";
import { Link } from "react-router-dom";
import SocialShare from "@/components/SocialShare";
import TableOfContents from "@/components/TableOfContents";
import Hero from "@/components/Header/Hero";
import { HeadSEO } from "@/components/SEO/HeadSEO";
import * as StyledLayout from "../Styled.Layout";
import * as StyledTypography from "@/styles/Styled.Typography";
import * as StyledArticleSingle from "./Styled.ArticleSingle";
import EmailSignup from "@/components/EmailSignup";

// Create proper heading components that render as actual heading tags
const createHeadingComponent = (Tag, StyledComponent) => {
  return ({ children, ...props }) => {
    // Generate ID from children text for TOC
    const id =
      typeof children === "string"
        ? children
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "")
        : undefined;

    return (
      <Tag id={id} {...props}>
        <StyledComponent>{children}</StyledComponent>
      </Tag>
    );
  };
};

const shortcodes = {
  Link,
  // Option 1: If styled components render as divs, wrap them in proper heading tags
  h1: createHeadingComponent("h1", StyledTypography.H1),
  h2: createHeadingComponent("h2", StyledTypography.H2),
  h3: createHeadingComponent("h3", StyledTypography.H3),
  h4: createHeadingComponent("h4", StyledTypography.H4),
  h5: createHeadingComponent("h5", StyledTypography.H5),
  h6: createHeadingComponent("h6", StyledTypography.H6),
  ul: StyledTypography.Ul,
  ol: StyledTypography.Ol,
  p: StyledTypography.P,
  blockquote: StyledTypography.Blockquote,
  strong: StyledTypography.Strong,
  StyledLink: StyledTypography.StyledLink,
};

export default function ArticleSingleLayout({
  heroProps,
  children,
  slug,
  title,
  description, // Add description prop
  image, // Add image prop
  readingTime,
  tags,
  author,
  // SEO props
  seoTitle,
  seoDescription,
  seoImage,
  seoImageAlt,
  noindex = false,
  structuredData,
  additionalMeta = [],
}) {
  const [contentRendered, setContentRendered] = useState(false);

  // Ensure content is marked as rendered after MDX processes
  useEffect(() => {
    // Use a microtask to ensure this runs after MDX rendering
    Promise.resolve().then(() => {
      setContentRendered(true);
    });
  }, [children]);

  return (
    <StyledLayout.PageLayoutWrapper>
      {/* Add SEO component */}
      <HeadSEO
        siteTitle={seoTitle || title}
        siteDescription={seoDescription || description}
        siteImage={seoImage || image}
        siteImageAlt={seoImageAlt}
        noindex={noindex}
        structuredData={structuredData}
        additionalMeta={additionalMeta}
      />

      <Hero {...heroProps} />
      <MDXProvider components={shortcodes}>
        <AnimateOnScroll>
          <StyledArticleSingle.GridContentWrapper>
            {/* SocialShare Component */}
            <StyledArticleSingle.GridItemContent>
              <SocialShare
                slug={slug}
                title={title}
                description={description}
                image={image}
              />
            </StyledArticleSingle.GridItemContent>

            {/* Article Details Component */}
            <StyledArticleSingle.GridItemContent>
              <StyledArticleSingle.GridInfoWrapper>
                <StyledArticleSingle.GridItemInfo>
                  <StyledTypography.TypoCaption2>
                    {readingTime} Min{readingTime !== 1 ? "s" : ""} Read
                  </StyledTypography.TypoCaption2>
                </StyledArticleSingle.GridItemInfo>
                <StyledArticleSingle.GridItemInfo>
                  <StyledTypography.TypoCaption1>
                    {author}
                  </StyledTypography.TypoCaption1>
                </StyledArticleSingle.GridItemInfo>
                <StyledArticleSingle.GridItemInfo>
                  <StyledArticleSingle.FlexInfoWrapper>
                    {tags &&
                      tags.map((tag, index) => (
                        <StyledArticleSingle.ChipLabel key={index}>
                          {tag}
                        </StyledArticleSingle.ChipLabel>
                      ))}
                  </StyledArticleSingle.FlexInfoWrapper>
                </StyledArticleSingle.GridItemInfo>
                <StyledArticleSingle.GridItemInfo>
                  <StyledLayout.Divider />
                </StyledArticleSingle.GridItemInfo>
              </StyledArticleSingle.GridInfoWrapper>
            </StyledArticleSingle.GridItemContent>

            {/* Main Content */}
            <StyledArticleSingle.GridItemContent>
              <StyledArticleSingle.ArticleContent id="article-content">
                {children}
              </StyledArticleSingle.ArticleContent>
            </StyledArticleSingle.GridItemContent>

            {/* Email Signup */}
            <StyledArticleSingle.GridItemContent data-animate="fade-up">
              <EmailSignup />
            </StyledArticleSingle.GridItemContent>

            {/* Table Of Contents Component - Only render after content is ready */}
            <StyledArticleSingle.GridItemContent>
              {contentRendered && (
                <TableOfContents
                  navbarHeight={58}
                  targetElementId="article-content"
                />
              )}
            </StyledArticleSingle.GridItemContent>
          </StyledArticleSingle.GridContentWrapper>
        </AnimateOnScroll>
      </MDXProvider>
    </StyledLayout.PageLayoutWrapper>
  );
}
